import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import Button, { ButtonSizes, ButtonTypes } from '@Components/Buttons/Button';
import FormField from '@Components/forms/FormField';
import Checkbox from '@Components/forms/inputs/Checkbox';
import TextInput, { TextInputProps } from '@Components/forms/inputs/TextInput';
import HorizontalRule from '@Components/HorizontalRule';
import InfoMessage, { MessageTypes } from '@Components/InfoMessage';
import Row from '@Components/layout/Row';
import SocialLogins from '@Components/SocialLogins';
import Typography from '@Components/Typography';
import { USER_TOURS, UserTourStates, UserTourSteps } from '@Config/constants';
import { Messages } from '@Config/messages';
import { useTranslations } from '@Hooks/useTranslations';
import { showUserTour } from '@Store/app/app.actions';
import { getUserIsLogged } from '@Store/auth/auth.selector';
import { validate, validateEmail, validatePassword, validateRequired, validateUsername } from '@Utils/form';
import { GAAction, GACategory, sendGaEvent } from '@Utils/gaEvents';
import { getFromLocalStorage, setToLocalStorage } from '@Utils/localStorage';

import styles from '../AuthenticationPage.module.scss';

enum RegisterFormFields {
  email = 'email',
  username = 'username',
  password = 'password',
  terms = 'conditionsAccepted',
  marketing = 'receiveMarketingMaterial',
}

export interface RegisterFormData {
  email: string;
  username: string;
  password: string;
  conditionsAccepted: boolean;
  receiveMarketingMaterial: boolean;
  addNaturalCompany: boolean;
}

interface RegisterFormProps {
  onSubmit: (values: any) => Promise<RegisterFormData>;
  initialValues?: RegisterFormData;
}

const renderForm = ({
  handleSubmit,
  submitting,
  submitError,
  initialValues,
}: FormRenderProps<RegisterFormData>): ReactNode => {
  const t = useTranslations();

  const onRegistrationCompleted = () => {
    sendGaEvent({
      category: GACategory.registrationForm,
      action: GAAction.register,
    });
  };

  const { addNaturalCompany } = initialValues;

  return (
    <form
      onSubmit={event => {
        handleSubmit(event)?.then(onRegistrationCompleted);
      }}
    >
      <InfoMessage type={MessageTypes.error} message={submitError} />
      <div data-tutorial-step={UserTourSteps.authenticationPage.authStep1}>
        <FormField<TextInputProps<string>>
          name={RegisterFormFields.email}
          type="text"
          component={TextInput}
          placeholder={Messages.labelEmail}
          validate={validate(validateRequired(t), validateEmail(t))}
        />
        <FormField<TextInputProps<string>>
          name={RegisterFormFields.username}
          type="text"
          component={TextInput}
          placeholder={Messages.labelUsername}
          validate={validate(validateRequired(t), validateUsername(t))}
        />
        <FormField<TextInputProps<string>>
          name={RegisterFormFields.password}
          component={TextInput}
          type="password"
          placeholder={Messages.labelPassword}
          validate={validate(validatePassword(t), validateRequired(t))}
        />
      </div>
      <div data-tutorial-step={UserTourSteps.authenticationPage.authStep2}>
        <Row constant alignCenter className={styles.lastInput}>
          <FormField name={RegisterFormFields.marketing} component={Checkbox} type="checkbox" />
          <Typography
            msg={t(Messages.registerMarketingMaterial)}
            tag="span"
            color="gray"
            size="m"
            className={styles.promoInfoAgreementText}
          />
        </Row>
        <Row constant alignCenter className={styles.lastInput}>
          <FormField
            name={RegisterFormFields.terms}
            component={Checkbox}
            type="checkbox"
            validate={validateRequired(t)}
            errorDisplayClassName={styles.errorDisplay}
          />
          <Typography msg={t(Messages.registerTermsOfUsage)} tag="span" color="gray" size="m" html />
        </Row>
      </div>
      <div data-tutorial-step={UserTourSteps.authenticationPage.authStep3}>
        <Button
          loading={submitting}
          label={t(Messages.register)}
          disabled={submitting}
          size={ButtonSizes.fill}
          type={ButtonTypes.submit}
        />
      </div>
      <HorizontalRule msg={t(Messages.labelOr)} marginTop />
      <SocialLogins addNaturalCompany={addNaturalCompany} />
    </form>
  );
};

const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit, initialValues }) => {
  const t = useTranslations();
  const isFirstLogin = !getFromLocalStorage(USER_TOURS.authenticationPageTour);
  const isNotLoggedIn = !useSelector(getUserIsLogged);
  const dispatch = useDispatch();

  const userTourSteps = [
    {
      target: UserTourSteps.authenticationPage.authStep1,
      content: t('msg_label_authentication_tour_auth_step1'),
    },
    {
      target: UserTourSteps.authenticationPage.authStep2,
      content: t('msg_label_authentication_tour_auth_step2'),
    },
    {
      target: UserTourSteps.authenticationPage.authStep3,
      content: t('msg_label_authentication_tour_auth_step3'),
    },
  ];

  React.useEffect(() => {
    sendGaEvent({
      category: GACategory.registrationForm,
      action: GAAction.render,
    });
    if (isFirstLogin && isNotLoggedIn) {
      dispatch(showUserTour(userTourSteps));
      setToLocalStorage(USER_TOURS.authenticationPageTour, UserTourStates.off);
    }
    return () => {
      dispatch(showUserTour([]));
    };
  }, []);

  return <Form<RegisterFormData> onSubmit={onSubmit} render={renderForm} initialValues={initialValues} />;
};

export default RegisterForm;
